import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import ChannelsGettingStartedHowToStartSrc from '../../../../assets/images/appsStoreGettingStartedChannelHowToStart.svg';
import ChannelsGettingStartedWhereToFindSrc from '../../../../assets/images/appsStoreGettingStartedChannelWhereToFind.svg';
import PluginsGettingStartedHowToStartSrc from '../../../../assets/images/appsStoreGettingStartedPluginHowToStart.svg';
import PluginsGettingStartedWhereToFindSrc from '../../../../assets/images/appsStoreGettingStartedPluginWhereToFind.svg';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {
  IntegrationTypeModel,
  IntegrationTypesSupportedTypesEnum,
} from '../../../../models/api/integrationTypesModel';
import {TeammateModel} from '../../../../models/api/teammateModel';

/*
 * Props.
 */

interface SettingsAppsDetailsGettingStartedPageProps {
  integrationType: IntegrationTypeModel;
  user: TeammateModel;
}

/*
 * Style.
 */

const StyledGettingStartedWrapperDiv = styled.div`
  max-width: 600px;
  padding-bottom: 28px;
`;

const StyledSectionWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 16px;

  &:not(:first-child) {
    margin-top: 32px;
  }
`;

const StyledLearnMoreSectionWrapperDiv = styled(StyledSectionWrapperDiv)`
  gap: 4px;
`;

const StyledH1 = styled.h1`
  font-size: 24px;
  line-height: 31px;
  font-weight: ${fontWeights.semibold};
  margin: 0;
`;

const StyledH2 = styled.h2`
  font-size: ${fontSizes.veryLarge};
  line-height: 24px;
  font-weight: ${fontWeights.semibold};
  margin: 0;
`;

const StyledDetailsP = styled.p`
  margin: 0;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};
  line-height: 20px;
`;

const StyledImg = styled.img`
  width: 100%;
`;

const StyledOl = styled.ol`
  margin: 0;
  padding-left: 16px;

  & > li {
    line-height: 20px;
    color: ${(p) => p.theme.greys.shade80};
    font-weight: ${fontWeights.normal};
  }
`;

const StyledLearnMoreTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  line-height: 19px;
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.normal};
`;

const StyledLearnMoreDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
`;

const StyledLearnMoreDetailsDiv = styled.div`
  flex: 1;
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade60};
  line-height: 22px;
`;

const StyledLearnMoreButtonWrapperDiv = styled.div`
  display: flex;
  height: 30px;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsDetailsGettingStartedPage: FC<SettingsAppsDetailsGettingStartedPageProps> = (
  props,
) => {
  const {integrationType, user} = props;
  const {navigateToLink} = useLinkNavigation();

  return (
    <StyledGettingStartedWrapperDiv>
      {maybeRenderPluginGettingStarted(integrationType, user)}
      {maybeRenderChannelGettingStarted(integrationType)}
      {maybeRenderOtherGettingStarted(integrationType)}
      {maybeRenderLearnMoreDetails(integrationType, navigateToLink)}
    </StyledGettingStartedWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderLearnMoreDetails(
  integrationType: IntegrationTypeModel,
  navigateToLink: HandlerOf<string>,
) {
  const {publisher} = integrationType;
  if (!publisher) {
    return null;
  }
  const isOtherType = integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.OTHER);

  return (
    <StyledLearnMoreSectionWrapperDiv>
      <StyledLearnMoreTitleDiv>
        <Trans id="settings-apps-details-getting-started-learn-more-title">Learn more about this app</Trans>
      </StyledLearnMoreTitleDiv>
      <StyledLearnMoreDetailsWrapperDiv>
        <StyledLearnMoreDetailsDiv>
          {!isOtherType && (
            <Trans id="settings-apps-details-getting-started-learn-more-description">
              Check out {integrationType.name}&apos;s help documents to learn more about this app and how it
              can help you.
            </Trans>
          )}
          {isOtherType && (
            <Trans id="settings-apps-details-getting-started-other-learn-more-description">
              Check out {integrationType.name}&apos;s setup guide to learn more about how to get started.
            </Trans>
          )}
        </StyledLearnMoreDetailsDiv>
        <StyledLearnMoreButtonWrapperDiv>
          <Button onClick={() => navigateToLink(publisher.support)}>
            <StyledButtonContentWrapperDiv>
              <Icon name="externalLinkSmall" />
              {!isOtherType && (
                <Trans id="settings-apps-details-getting-started-learn-more-button">Open app docs</Trans>
              )}
              {isOtherType && (
                <Trans id="settings-apps-details-getting-started-other-learn-more-button">
                  View setup guide
                </Trans>
              )}
            </StyledButtonContentWrapperDiv>
          </Button>
        </StyledLearnMoreButtonWrapperDiv>
      </StyledLearnMoreDetailsWrapperDiv>
    </StyledLearnMoreSectionWrapperDiv>
  );
}

function maybeRenderPluginGettingStarted(integrationType: IntegrationTypeModel, user: TeammateModel) {
  if (!integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN)) {
    return null;
  }
  return (
    <>
      <StyledSectionWrapperDiv>
        <StyledH1>
          <Trans id="settings-apps-details-getting-started-plugin-title">Using plugins</Trans>
        </StyledH1>
        <StyledH2>
          <Trans id="settings-apps-details-getting-started-plugin-where-to-find-title">
            Where to find this app
          </Trans>
        </StyledH2>
        <StyledDetailsP>
          <Trans id="settings-apps-details-getting-started-plugin-where-to-find-details">
            Plugins can be accessed via the right sidebar in Front and can react to the current conversation
            or customer you&apos;re communicating with. By embedding your CRM, project management tool, phone
            dialers and more, they provide easy access to key context and actions right from your inbox.
          </Trans>
        </StyledDetailsP>
        <StyledImg src={PluginsGettingStartedWhereToFindSrc} alt="Where to find plugins" />
      </StyledSectionWrapperDiv>
      <StyledSectionWrapperDiv>
        <StyledH2>
          <Trans id="settings-apps-details-getting-started-plugin-how-to-start-title">
            How to start using this app
          </Trans>
        </StyledH2>
        <StyledOl>
          <li>
            <Trans id="settings-apps-details-getting-started-plugin-how-to-start-item-1">
              Return to your inbox
            </Trans>
          </li>
          <li>
            <Trans id="settings-apps-details-getting-started-plugin-how-to-start-item-2">
              Click the plugin logo in your right sidebar to open it. If you don&apos;t see the plugin, ensure
              it is pinned by opening the Manage button at the bottom of the sidebar and clicking the pin icon
              button on the plugin card.
            </Trans>
          </li>
          <li>
            <Trans id="settings-apps-details-getting-started-plugin-how-to-start-item-3">
              Sign in with your account credentials
            </Trans>
          </li>
        </StyledOl>
        <StyledImg src={PluginsGettingStartedHowToStartSrc} alt="How to get started" />
      </StyledSectionWrapperDiv>
    </>
  );
}

function maybeRenderChannelGettingStarted(integrationType: IntegrationTypeModel) {
  if (!integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL)) {
    return null;
  }
  return (
    <>
      <StyledSectionWrapperDiv>
        <StyledH1>
          <Trans id="settings-apps-details-getting-started-channel-title">Using channels</Trans>
        </StyledH1>
        <StyledH2>
          <Trans id="settings-apps-details-getting-started-channel-where-to-find-title">
            Where to find this app
          </Trans>
        </StyledH2>
        <StyledDetailsP>
          <Trans id="settings-apps-details-getting-started-channel-where-to-find-details">
            Channel integrations allow you to connect with customers regardless of where they are reaching
            out, from live chat to SMS to social. To begin using a channel, simply add it to any team or
            individual inbox you have access to.
          </Trans>
        </StyledDetailsP>
        <StyledImg src={ChannelsGettingStartedHowToStartSrc} alt="Where to find channels" />
      </StyledSectionWrapperDiv>
      <StyledSectionWrapperDiv>
        <StyledH2>
          <Trans id="settings-apps-details-getting-started-channel-how-to-start-title">
            How to start using this app
          </Trans>
        </StyledH2>
        <StyledOl>
          <li>
            <Trans id="settings-apps-details-getting-started-channel-how-to-start-item-1">
              Click on <strong>Inboxes</strong> in the left menu for the workspace you&apos;d like to add the
              channel to.
            </Trans>
          </li>
          <li>
            <Trans id="settings-apps-details-getting-started-channel-how-to-start-item-2">
              Select an existing inbox or create a new one.
            </Trans>
          </li>
          <li>
            <Trans id="settings-apps-details-getting-started-channel-how-to-start-item-3">
              From the <strong>Channels</strong> tab, select the type of channel you&apos;d like to connect.
            </Trans>
          </li>
          <li>
            <Trans id="settings-apps-details-getting-started-channel-how-to-start-item-4">
              Follow the steps on screen to complete setting up for the channel type you&apos;ve chosen.
            </Trans>
          </li>
        </StyledOl>
        <StyledImg src={ChannelsGettingStartedWhereToFindSrc} alt="How to get started" />
      </StyledSectionWrapperDiv>
    </>
  );
}

function maybeRenderOtherGettingStarted(integrationType: IntegrationTypeModel) {
  if (!integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.OTHER)) {
    return null;
  }
  return (
    <>
      <StyledSectionWrapperDiv>
        <StyledH1>
          <Trans id="settings-apps-details-getting-started-other-title">Using apps</Trans>
        </StyledH1>
        <StyledH2>
          <Trans id="settings-apps-details-getting-started-other-where-to-find-title">
            Where to find this app
          </Trans>
        </StyledH2>
        <StyledDetailsP>
          <Trans id="settings-apps-details-getting-started-other-where-to-find-details">
            Apps can extend Front&apos;s capabilities in a variety of ways, from syncing data with external
            systems to adding rule actions to automating common actions. With so many possibilities, apps
            allow you to tailor your workflows to get the most out of Front.
          </Trans>
        </StyledDetailsP>
      </StyledSectionWrapperDiv>
      <StyledSectionWrapperDiv>
        <StyledH2>
          <Trans id="settings-apps-details-getting-started-other-how-to-start-title">
            How to start using this app
          </Trans>
        </StyledH2>
        <StyledOl>
          <li>
            <Trans id="settings-apps-details-getting-started-other-how-to-start-item-1">
              Click <strong>View setup guide</strong> below
            </Trans>
          </li>
          <li>
            <Trans id="settings-apps-details-getting-started-other-how-to-start-item-2">
              Complete the instructions in the guide. You may have to sign in or create an account with a
              third party service.
            </Trans>
          </li>
        </StyledOl>
      </StyledSectionWrapperDiv>
    </>
  );
}
